import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Phone, Plus, UserPlus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { ISD_CODE, formatIndianMobile } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Pick a customer, or make one on the spot.
 *
 * A counter does not have the customer's record to hand — it has the person,
 * and a phone number. So this searches by number as readily as by name, and
 * when nothing matches it offers to create the customer from exactly what was
 * typed. Two fields, because a bill cannot wait on an address: a name and a
 * mobile is enough to invoice, enough to find them next time, and enough to
 * send the bill on WhatsApp afterwards.
 *
 * The search runs on the server rather than filtering a loaded page, so it
 * finds the ten-thousandth customer as easily as the tenth.
 */
const DIGITS = /\D/g;
const looksLikePhone = (s) => s.replace(DIGITS, "").length >= 6;

export default function CustomerPicker({ value, onChange, disabled }) {
  const { organization } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ display_name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const searchRef = useRef(null);
  const nameRef = useRef(null);

  // Resolve whatever id we were handed, so the trigger can name the customer
  // on an invoice opened for editing.
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.id === value) return;
    api.get(`/parties/${value}`).then(({ data }) => setSelected(data)).catch(() => {});
  }, [value, selected?.id]);

  const search = useCallback((text) => {
    setLoading(true);
    api.get("/parties", { params: { kind: "CUSTOMER", q: text || undefined, page_size: 20 } })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => search(query.trim()), 220);
    return () => clearTimeout(t);
  }, [open, query, search]);

  const choose = (p) => {
    setSelected(p);
    onChange(p.id);
    setOpen(false);
    setQuery("");
    setCreating(false);
  };

  const startCreate = () => {
    const typed = query.trim();
    // Whatever they typed is almost certainly one of the two fields; put it in
    // the right one so nothing has to be retyped.
    setDraft(looksLikePhone(typed)
      // Keep only the local part: the +91 is shown beside the box, so a
      // pasted "+91 98765 43210" must not become "+91 +91 98765 43210".
      ? { display_name: "", phone: typed.replace(DIGITS, "").slice(-10) }
      : { display_name: typed, phone: "" });
    setCreating(true);
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const create = async () => {
    const name = draft.display_name.trim();
    const phone = draft.phone.trim();
    if (!name) return toast.error("Give the customer a name");
    if (phone.replace(DIGITS, "").length < 10)
      return toast.error("A mobile number is 10 digits — it is how you will find them again");
    setBusy(true);
    try {
      const { data } = await api.post("/parties", {
        // Stored with the country code, so the WhatsApp link built from it
        // later reaches a real phone rather than a ten-digit fragment.
        display_name: name, phone: formatIndianMobile(phone), is_customer: true,
        registration_type: "UNREGISTERED",
        // A walk-in is local unless told otherwise, and an invoice cannot be
        // issued without a place of supply.
        state_code: organization?.state_code || null,
      });
      (data.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`${data.display_name} added`);
      choose(data);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const label = selected
    ? `${selected.display_name}${selected.phone ? ` · ${selected.phone}` : ""}`
    : "Search by name or mobile…";

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreating(false); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" disabled={disabled}
          data-testid="invoice-party-select"
          className={`w-full justify-between font-normal ${selected ? "" : "text-slate-500"}`}>
          <span className="truncate">{label}</span>
          <ChevronsUpDown size={15} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {creating ? (
          <div className="space-y-3 p-3" data-testid="party-quick-add">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <UserPlus size={15} className="text-blue-600" /> New customer
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input ref={nameRef} className="h-9" data-testid="quick-add-name"
                value={draft.display_name} placeholder="Customer name"
                onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && create()} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mobile</Label>
              <div className="flex">
                <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-slate-50 px-2.5 font-mono text-sm text-slate-600">
                  +{ISD_CODE}
                </span>
                <Input className="h-9 rounded-l-none font-mono" data-testid="quick-add-phone"
                  inputMode="tel" maxLength={13} value={draft.phone} placeholder="98765 43210"
                  onChange={(e) => setDraft((d) => ({
                    ...d, phone: e.target.value.replace(/[^0-9 ]/g, "") }))}
                  onKeyDown={(e) => e.key === "Enter" && create()} />
              </div>
              <p className="text-[11px] leading-snug text-slate-500">
                Ten digits. Used to find this customer next time, and to send
                them the bill on WhatsApp.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1"
                onClick={() => setCreating(false)} data-testid="quick-add-cancel">Back</Button>
              <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={busy} onClick={create} data-testid="quick-add-save">
                {busy ? "Adding…" : "Add & select"}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="border-b border-slate-200 p-2">
              <Input ref={searchRef} autoFocus className="h-9" data-testid="party-search"
                value={query} placeholder="Name or mobile number"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !rows.length) { e.preventDefault(); startCreate(); }
                }} />
            </div>
            <div className="max-h-64 overflow-y-auto p-1" data-testid="party-results">
              {rows.map((p) => (
                <button key={p.id} data-testid={`party-option-${p.id}`}
                  onClick={() => choose(p)}
                  className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm transition-colors duration-150 hover:bg-slate-100">
                  <Check size={14}
                    className={value === p.id ? "shrink-0 text-blue-600" : "shrink-0 opacity-0"} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{p.display_name}</span>
                    <span className="block truncate text-[11px] text-slate-500">
                      {p.phone ? (
                        <span className="inline-flex items-center gap-1">
                          <Phone size={10} /> {p.phone}
                        </span>
                      ) : null}
                      {p.gstin ? `${p.phone ? " · " : ""}${p.gstin}` : ""}
                    </span>
                  </span>
                </button>
              ))}
              {!rows.length && (
                <div className="px-2 py-6 text-center text-xs text-slate-500"
                  data-testid="party-no-results">
                  {loading ? "Searching…"
                    : query.trim() ? "No customer matches that."
                    : "Start typing a name or mobile number."}
                </div>
              )}
            </div>
            <div className="border-t border-slate-200 p-1">
              <button onClick={startCreate} data-testid="party-add-new"
                className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm font-medium text-blue-700 transition-colors duration-150 hover:bg-blue-50">
                <Plus size={14} />
                {query.trim() ? `Add “${query.trim()}” as a new customer` : "Add a new customer"}
              </button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
